import React, { useMemo, Suspense, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import RouteFallback from "@/components/RouteFallback";
import {
  Home, Calendar, Users, User, LayoutGrid, Eye,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import useRealtime from "@/hooks/useRealtime";
import PromoExpiredDialog from "@/components/PromoExpiredDialog";
import TermsGate from "@/components/TermsGate";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import SideNav from "@/components/SideNav";
import AiAssistant from "@/components/AiAssistant";
import { usePlatformConfig } from "@/lib/platformConfig";
import { cn } from "@/lib/utils";

const BOTTOM_TABS = [
  { key: "home", to: "/", label: "Home", icon: Home, testID: "tab-home", end: true },
  { key: "events", to: "/events", label: "Events", icon: Calendar, testID: "tab-events" },
  { key: "community", to: "/community", label: "Community", icon: LayoutGrid, testID: "tab-community" },
  { key: "connections", to: "/connections", label: "Connections", icon: Users, testID: "tab-connections" },
  { key: "me", to: "/me", label: "My 2°", icon: User, testID: "tab-me" },
];

// Nav key -> platform feature module. Items without a mapping are never gated.
const NAV_FEATURE = {
  events: "events", community: "community", jobs: "jobs",
  learning: "learning", rewards: "rewards", wallet: "wallet", messages: "messages",
  projects: "projects", docs: "docs", calendar: "calendar", connections: "connections",
  expenses: "expenses", myhr: "hr", social: "social", store: "store", press: "press",
};
const featOn = (features, key) => {
  const m = NAV_FEATURE[key];
  return !m || !features || features[m] !== false;
};

function MobileTabs() {
  const { features, comingSoon } = useAuth();
  const { navLabel } = usePlatformConfig();
  const isSoon = (key) => (comingSoon || []).includes(NAV_FEATURE[key]);
  const tabs = useMemo(() => BOTTOM_TABS.filter((t) => featOn(features, t.key) || isSoon(t.key)), [features, comingSoon]);
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-surface-2 border-t border-line flex items-center justify-around h-16 px-2">
      {tabs.map(({ key, to, label, icon: Icon, testID, end }) => {
        const soon = isSoon(key);
        if (soon) {
          return (
            <button key={to} data-testid={testID} onClick={() => toast(`${navLabel("member", key, label)} is coming soon`)}
              className="flex flex-col items-center gap-0.5 flex-1 py-1 text-muted-foreground opacity-60 relative">
              <Icon className="w-5 h-5" />
              <span className="text-[10px] font-sans">{navLabel("member", key, label)}</span>
              <span data-testid={`${testID}-soon`} className="absolute top-0 right-1/4 text-[7px] uppercase font-semibold bg-amber-100 text-amber-800 border border-amber-300 px-1 rounded-full">Soon</span>
            </button>
          );
        }
        return (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testID}
            className={({ isActive }) =>
              cn("flex flex-col items-center gap-0.5 flex-1 py-1", isActive ? "text-brand" : "text-muted-foreground")
            }
          >
            <Icon className="w-5 h-5" />
            <span className="text-[10px] font-sans">{navLabel("member", key, label)}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function AppLayout() {
  const { user, logout, previewMember, canPreview, setPreviewMember } = useAuth();
  const location = useLocation();
  useRealtime();
  // Lightweight session tracking for analytics (fire-and-forget, 4-min heartbeat).
  useEffect(() => {
    if (!user) return;
    const ping = () => { api("/analytics/session/ping", { method: "POST" }).catch(() => {}); };
    ping();
    const id = setInterval(ping, 4 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  const { data: notif } = useQuery({
    queryKey: ["notifications-badge"],
    queryFn: () => api("/notifications"),
    refetchInterval: 30_000,
  });
  const unread = notif?.unread || 0;
  const { data: summary } = useQuery({
    queryKey: ["unread-summary"],
    queryFn: () => api("/unread-summary"),
    refetchInterval: 20_000,
  });
  const msgUnread = summary?.messages || 0;
  const { data: adminStats } = useQuery({
    queryKey: ["admin-pending-badge"],
    queryFn: () => api("/admin/stats"),
    enabled: !!user?.is_admin && !previewMember,
    refetchInterval: 30_000,
  });
  const pending = adminStats?.pending || 0;
  const realIsBusiness = user?.account_type === "business";
  const { data: mgr } = useQuery({ queryKey: ["nav-manageable"], queryFn: () => api("/business/manageable"), staleTime: 60_000 });
  const realManages = realIsBusiness || (mgr?.businesses?.length || 0) > 0;
  // While previewing as a member, suppress all business-owner navigation.
  const isBusiness = !previewMember && realIsBusiness;
  const managesBusiness = !previewMember && realManages;
  const { data: rewards } = useQuery({ queryKey: ["rewards-summary"], queryFn: () => api("/rewards/summary"), refetchInterval: 60_000, staleTime: 30_000 });
  const points = rewards?.balance ?? user?.points_balance ?? 0;

  return (
    <div className="min-h-screen bg-surface">
      <SideNav points={points} msgUnread={msgUnread} notifUnread={unread} pending={pending} managesBusiness={managesBusiness} isBusiness={isBusiness} canPreview={canPreview} previewMember={previewMember} onTogglePreview={() => setPreviewMember(!previewMember)} onLogout={logout} />

      {/* Main content */}
      <div className="sn-content min-w-0 flex flex-col">
        {previewMember && (
          <div data-testid="preview-member-banner" className="sticky top-0 z-40 flex items-center justify-center gap-3 bg-ink text-white text-sm px-4 py-2">
            <Eye className="w-4 h-4" />
            <span>You&apos;re previewing Two Degrees as a regular member.</span>
            <button
              type="button"
              data-testid="exit-preview-btn"
              onClick={() => setPreviewMember(false)}
              className="ml-1 rounded-pill bg-white/15 hover:bg-white/25 transition-colors px-3 py-0.5 font-semibold"
            >
              Exit preview
            </button>
          </div>
        )}
        <AnnouncementBanner />

        <main key={location.pathname} className={cn("flex-1 w-full animate-rise", location.pathname.startsWith("/docs") ? "px-4 sm:px-6 py-6 pb-24 lg:pb-10" : "max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-24 lg:pb-10")}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <MobileTabs />
      <AiAssistant />
      <PromoExpiredDialog />
      <TermsGate />
    </div>
  );
}
